import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import {
    type DriversListFiltersFormValues,
    EmploymentFilterValidationSchema,
    PersonalInformationFilterValidationSchema,
    DriverAddressFilterValidationSchema,
    LinkedDriverFilterValidationSchema,
    LicenceInformationFilterValidationSchema,
} from "./DriversListFiltersFormValues";
import * as yup from "yup";
import FilterPersonalInformation from "./FilterPersonalInformation";
import { errorToast } from "../../../toast";
import clsx from "clsx";
import Button from "../../Button";
import FilterEmployment from "./FilterEmployment";
import FilterDriverAddress from "./FilterDriverAddress";
import FilterLinkedDriver from "./FilterLinkedDriver";
import FilterLicenceInformation from "./FilterLicenceInformation";
import {
    DriverCategory,
    DriverCategoryOptionsType,
    driverPageLocation,
} from "../../../utils/driverPageLocation";
import { useLocation } from "react-router-dom";
import { useSelectedValues } from "./useSelectedValues";
import FilterLicenceAnalysis from "./FilterLicenceAnalysis";
import FilterVehicleAnalysis from "./FilterVehicleAnalysis";
import { driverListFiltersDefaultValues } from "./driversListFiltersDefaultValues";
import _ from "lodash";
import useTableStore from "../../../stores/useTableStore";

const validationSchema = yup.object().shape({
    company: yup.object().shape(EmploymentFilterValidationSchema),
    personalInformation: yup
        .object()
        .shape(PersonalInformationFilterValidationSchema),
    driverAddress: yup.object().shape(DriverAddressFilterValidationSchema),
    linkedDriver: yup.object().shape(LinkedDriverFilterValidationSchema),
    licence: yup.object().shape(LicenceInformationFilterValidationSchema),
});
export type DriversListFiltersFormProps = {
    filterValues: DriversListFiltersFormValues | undefined;
    onSaveFilter: (value: DriversListFiltersFormValues) => void;
    handleCloseEditDialog: () => void;
    isApplyingFilter: boolean;
};

const DriversListFiltersForm: React.FC<DriversListFiltersFormProps> = ({
    filterValues,
    onSaveFilter,
    handleCloseEditDialog,
    isApplyingFilter,
}) => {
    const appliedFilterAllDrivers = useTableStore(
        (state) => state.appliedFilterAllDrivers
    );

    const setAppliedFilter = useTableStore((state) => state.setAppliedFilter);

    const methods = useForm<DriversListFiltersFormValues>({
        mode: "onSubmit",
        resolver: yupResolver(validationSchema),
        defaultValues: filterValues,
    });

    const pageLocation = useLocation();

    const location = driverPageLocation(
        pageLocation.pathname
    ) as DriverCategoryOptionsType;

    const checkIsNothingSelected = (values: DriversListFiltersFormValues) => {
        if (
            JSON.stringify(values) ===
            JSON.stringify(driverListFiltersDefaultValues(location))
        ) {
            errorToast("No filters are selected");
            return true;
        }

        return false;
    };

    const removeEmptyOrNullValues = (values: DriversListFiltersFormValues) => {
        const removeNullValues = (
            object: { [key: string]: any } | undefined
        ) => {
            if (!object) return undefined;

            const objectWithNoNullValues = Object.entries(object).reduce(
                (acc: { [key: string]: any }, [key, value]) => {
                    if (value !== null && value !== undefined) {
                        acc[key] =
                            typeof value === "object"
                                ? removeNullValues(value)
                                : value;
                    }
                    return acc;
                },
                {}
            );

            return !_.isEmpty(objectWithNoNullValues)
                ? objectWithNoNullValues
                : undefined;
        };

        /** Only way to do this is to go through each object individaully and tell it not to add anything. */
        const filters: DriversListFiltersFormValues = {
            ...(values.driverIds?.length !== 0 && {
                driverIds: values.driverIds,
            }),
            ...(values.riskProfiles?.length !== 0 && {
                riskProfiles: values.riskProfiles,
            }),
            ...(values.greyFleet && { greyFleet: true }),
            ...(!_.isEmpty(values.company) && {
                company: removeNullValues(values.company),
            }),
            ...(!_.isEmpty(values.personalInformation) && {
                personalInformation: removeNullValues(
                    values.personalInformation
                ),
            }),
            ...(!_.isEmpty(values.driverAddress) && {
                driverAddress: removeNullValues(values.driverAddress),
            }),
            ...(!_.isEmpty(values.linkedDriver) && {
                linkedDriver: removeNullValues(values.linkedDriver),
            }),
            ...(!_.isEmpty(values.licence) && {
                licence: removeNullValues(values.licence),
            }),
            ...(!_.isEmpty(values.dvlaQuery) && {
                dvlaQuery: removeNullValues(values.dvlaQuery),
            }),
            ...(!_.isEmpty(values.greyFleetQuery) && {
                greyFleetQuery: removeNullValues(values.greyFleetQuery),
            }),
            ...(!_.isEmpty(values.declarationsQuery) && {
                declarationsQuery: removeNullValues(values.declarationsQuery),
            }),
            ...(!_.isEmpty(values.driverLicenceAnalysis) && {
                driverLicenceAnalysis: removeNullValues(
                    values.driverLicenceAnalysis
                ),
            }),
            ...(!_.isEmpty(values.vehicleAnalysis) && {
                vehicleAnalysis: removeNullValues(values.vehicleAnalysis),
            }),
            ...(!_.isEmpty(values.driverAnalysis) && {
                driverAnalysis: removeNullValues(values.driverAnalysis),
            }),
            ...(!_.isEmpty(values.licenceChanges) && {
                licenceChanges: removeNullValues(values.licenceChanges),
            }),
            ...(values.professional && {
                professional: true,
            }),
        };

        return filters;
    };

    const handleFormSubmit = (filters: DriversListFiltersFormValues) => {
        const nothingIsSelected = checkIsNothingSelected(filters);
        if (nothingIsSelected) return;

        const shorthandFilters = removeEmptyOrNullValues(filters);

        /** No filterId or name when setting filters from form */
        setAppliedFilter(
            {
                filterId: "",
                filterFields: shorthandFilters,
                name: "",
            },
            location
        );

        handleCloseEditDialog();
    };

    const handleSaveFilter = (value: DriversListFiltersFormValues) => {
        const nothingIsSelected = checkIsNothingSelected(value);
        if (nothingIsSelected) return;

        const shorthandFilters = removeEmptyOrNullValues(value);
        onSaveFilter(shorthandFilters);
    };

    // values for summary in each dropdown section
    const { selectedValues, setSelectedValues, clearSelectedValues } =
        useSelectedValues(methods);

    const handleClearAll = () => {
        const defaultValues = driverListFiltersDefaultValues(location);

        methods.reset(defaultValues);
        clearSelectedValues();

        /** Clear filters in global state */
        setAppliedFilter(
            {
                ...appliedFilterAllDrivers,
                filterFields: defaultValues,
            },
            location
        );

        handleCloseEditDialog();
    };

    return (
        <form onSubmit={methods.handleSubmit(handleFormSubmit)}>
            <div className="max-h-[32rem] overflow-y-auto">
                <FilterPersonalInformation
                    methods={methods}
                    selectedValues={selectedValues.personalInformation}
                    setSelectedValues={setSelectedValues.personalInformation}
                />
                <FilterEmployment
                    methods={methods}
                    selectedValues={selectedValues.employment}
                    setSelectedValues={setSelectedValues.employment}
                />
                <FilterDriverAddress
                    methods={methods}
                    selectedValues={selectedValues.driverAddress}
                    setSelectedValues={setSelectedValues.driverAddress}
                />
                <FilterLinkedDriver
                    methods={methods}
                    selectedValues={selectedValues.linkedDriver}
                    setSelectedValues={setSelectedValues.linkedDriver}
                />
                <FilterLicenceInformation
                    methods={methods}
                    selectedValues={selectedValues.licence}
                    setSelectedValues={setSelectedValues.licence}
                    location={location}
                />
                {location !== "greyfleet" && (
                    <FilterLicenceAnalysis methods={methods} />
                )}
                {location === "greyfleet" && (
                    <FilterVehicleAnalysis methods={methods} />
                )}
            </div>
            <div className="flex flex-col-reverse justify-between rounded-b-sm border-t border-S2D-neutral-80 bg-S2D-neutral-90 py-3 px-6 md:flex-row">
                <Button
                    variant="tertiary"
                    type="button"
                    onClick={handleClearAll}
                    className="w-full justify-center md:w-auto"
                >
                    Clear all
                </Button>
                <div
                    className={clsx(
                        "flex flex-col-reverse md:flex-row md:justify-between md:gap-2"
                    )}
                >
                    <div className={clsx("py-2 md:py-0")}>
                        <Button
                            variant="secondary"
                            type="button"
                            className="w-full justify-center md:w-auto"
                            onClick={methods.handleSubmit(handleSaveFilter)}
                        >
                            Save filters
                        </Button>
                    </div>
                    <Button
                        type="submit"
                        loading={isApplyingFilter}
                        className="w-full justify-center md:w-auto"
                    >
                        Apply filters
                    </Button>
                </div>
            </div>
        </form>
    );
};

export default DriversListFiltersForm;
